import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { connectRefinementList } from 'react-instantsearch-dom'
import Downshift from 'downshift'
import { usePopper } from 'react-popper'
import { FACET_WHITE_LIST } from '../../domain/term'
import IconChevronLeft from '@/components/IconChevronLeft'
import IconChevronRight from '@/components/IconChevronRight'
import IconChevronDownChipFilter from '@/components/IconChevronDownChipFilter'
import { Search } from 'react-bootstrap-icons'
import formatCount from '../../utils/formatCount'
import {
  FILTER_SELECTORS_STATIC_DATA,
  FILTER_SELECTORS_LABELS,
  WORLDWIDE_SEARCH_LOCATION_NAME,
  FACET_PAGE_FILTERS
} from '../../domain/filterOptions/constants'
import { isPhoneScreen } from '../../utils/screenBreakpointsUtils'
import FilterBottomSheet from './FilterBottomSheet'
import FilterDropdown from './FilterDropdown'
import {
  locationListHasUsOrCanada,
  locationListHasUsOrUkOrCanada
} from '../../domain/location/useCases/computeLocationGroups'
import useStaticRandomId from '../../hooks/useStaticRandomId'
import Tooltip from '@/components/atoms/Tooltip/Tooltip'
import { isNil } from '../../utils/checks'
import refineSearchAndNavigateToBrowsePageWithQueryParams from '../../domain/search/refineSearchAndNavigateToBrowsePageWithQueryParams'
import clearRefinementFromSearchState from '../../domain/search/clearRefinementFromSearchState'
import { navigateToBrowsePageWithQueryParams } from '../../domain/search/navigateToBrowsePageWithQueryParams'
import noop from '../../utils/noop'
import { createPrimaryFocusMap } from '@/domain/term/useCases/createPrimaryFocusMap'
import { Department } from '../../domain/department'
import getAllowedFilters from '../../domain/filterOptions/useCases/getAllowedFilters'

const TOP_BAR_PLACEHOLDER_HEIGHT_OFFSET = 84
const defaultHiddenAttributes = []
const defaultFilterableTaxonomies = []
const defaultNoopFunction = noop

const setSearchState = (label, getTaxonomy, isFacetOrLocationPage) => {
  const item = getTaxonomy()
  const { taxonomy: type, value } = item
  refineSearchAndNavigateToBrowsePageWithQueryParams(
    {
      label,
      type,
      remove: !value.includes(label)
    },
    isFacetOrLocationPage
  )
}

const FilterTopBar = ({
  locationName,
  searchState,
  hiddenAttributes = defaultHiddenAttributes,
  topBarStyles = 'max-width: 1400px;',
  filterableTaxonomies = defaultFilterableTaxonomies,
  facetPage,
  includeInsurance = true,
  geoLocation,
  locationAddressParts = null,
  department,
  scrollToFilterBar = defaultNoopFunction
}) => {
  const topBarPlaceholderRef = useRef()
  const topBarRef = useRef()
  const [slideLeft, setSlideLeft] = useState(0)
  const [showFilterTopBarBorder, setShowFilterTopBarBorder] = useState(false)

  // defaults to true if locationAddressParts not provided
  const isUSorUKorCanada = useMemo(
    () =>
      isNil(locationAddressParts) ||
      locationListHasUsOrUkOrCanada(locationAddressParts),
    [locationAddressParts]
  )

  const isUSorCanada = useMemo(
    () =>
      isNil(locationAddressParts) ||
      locationListHasUsOrCanada(locationAddressParts),
    [locationAddressParts]
  )

  useEffect(() => {
    let mouseDown,
      mouseUp,
      mouseLeave,
      mouseMove,
      topBarScroll,
      toggleStickyClass

    if (topBarRef.current) {
      let isDown = false
      let startX
      let scrollLeft

      mouseDown = (e) => {
        isDown = true
        startX = e.pageX - topBarRef.current.offsetLeft
        scrollLeft = topBarRef.current.scrollLeft
      }

      mouseUp = () => {
        isDown = false
      }

      mouseLeave = () => {
        isDown = false
      }

      mouseMove = (e) => {
        if (!isDown) return
        e.preventDefault()
        const x = e.pageX - topBarRef.current.offsetLeft
        const walk = x - startX //scroll-fast
        topBarRef.current.scrollLeft = scrollLeft - walk
      }
      topBarRef.current.addEventListener('mousedown', mouseDown)
      topBarRef.current.addEventListener('mouseleave', mouseLeave)
      topBarRef.current.addEventListener('mouseup', mouseUp)
      topBarRef.current.addEventListener('mousemove', mouseMove)

      topBarScroll = () => {
        setSlideLeft(topBarRef.current.scrollLeft)
      }
      topBarRef.current.addEventListener('scroll', topBarScroll)

      toggleStickyClass = () => {
        const topBarRefPosition = topBarRef?.current?.getBoundingClientRect()
        if (topBarRefPosition?.top <= TOP_BAR_PLACEHOLDER_HEIGHT_OFFSET) {
          setShowFilterTopBarBorder(true)
        } else if (topBarRefPosition?.top > TOP_BAR_PLACEHOLDER_HEIGHT_OFFSET) {
          setShowFilterTopBarBorder(false)
        }
      }

      window.addEventListener('scroll', toggleStickyClass)
    }

    return () => {
      window.removeEventListener('scroll', toggleStickyClass)
      if (topBarRef.current) {
        topBarRef.current.removeEventListener('mousedown', mouseDown)
        topBarRef.current.removeEventListener('mousedown', mouseLeave)
        topBarRef.current.removeEventListener('mousedown', mouseUp)
        topBarRef.current.removeEventListener('mousedown', mouseMove)
        topBarRef.current.removeEventListener('scroll', topBarScroll)
      }
    }
  }, [])

  const { sortedFilters } = useMemo(() => {
    const sortedFilters = FILTER_SELECTORS_STATIC_DATA.filter((item) => {
      if (facetPage && !FACET_PAGE_FILTERS.includes(item.attribute)) {
        return false
      }

      if (
        !isUSorUKorCanada &&
        item.label === FILTER_SELECTORS_LABELS.INSURANCE
      ) {
        return false
      }

      return true
    })
      .map((item) => {
        const relatedTaxonomies = filterableTaxonomies?.filter(
          (i) => i.type === (item.taxonomyType || item.attribute)
        )

        return {
          ...item,
          multiSelect:
            facetPage && FACET_WHITE_LIST.includes(item.attribute)
              ? false
              : item.multiSelect,
          itemWhitelist: relatedTaxonomies?.map((i) => i.title),
          itemBlacklist: item.itemBlacklist || []
        }
      })
      .map((filter) => {
        const selectedRefinement = searchState[filter.attribute]
        let selectedItems = [selectedRefinement].flat().filter(Boolean)

        selectedItems = getAllowedFilters(
          selectedItems,
          filter.itemWhitelist,
          filter.itemBlacklist,
          (l) => l
        )

        if (filter.refinement) {
          selectedItems = selectedItems.filter((i) => i === filter.refinement)
        }

        const updatedSelector = {
          ...filter,
          selectedItems
        }

        if (filter.hidden && !hiddenAttributes.includes(filter.attribute)) {
          hiddenAttributes.push(filter.attribute)
        }

        return {
          ...updatedSelector,
          isSelected: selectedItems.length > 0
        }
      })
      .sort((a, b) => {
        return b.isSelected - a.isSelected
      })

    return { sortedFilters }
  }, [
    includeInsurance,
    filterableTaxonomies,
    facetPage,
    searchState,
    topBarRef,
    scrollToFilterBar,
    hiddenAttributes,
    isUSorUKorCanada
  ])

  const clearFilters = useCallback(
    (taxonomy) => {
      const clearedState = clearRefinementFromSearchState(taxonomy)
      navigateToBrowsePageWithQueryParams(clearedState)
    },
    [scrollToFilterBar]
  )

  return (
    <div className="px-4 md:px-0">
      <div
        className={`${
          showFilterTopBarBorder ? 'border-b border-gray-300' : ''
        } ${!facetPage ? 'lg:border-0 ' : ''}
        `}
      >
        <div ref={topBarPlaceholderRef} />
        {/* FilterTopBarContainer */}
        <div className="flex items-center bg-white py-3">
          {/* LeftArrow */}
          <div
            className={`absolute left-0 top-0 hidden h-[62px] w-20 shrink-0 justify-start bg-gradient-to-r from-white via-white to-transparent md:flex md:items-center ${
              slideLeft > 0 ? '' : 'md:hidden'
            }`}
          >
            <div
              className={`z-10 mb-0.5 flex size-9
             shrink-0 cursor-pointer items-center justify-center rounded-full border border-gray-400 bg-white px-1 py-2
              shadow-md hover:bg-gray-50 active:bg-gray-200 active:shadow-none`}
              onClick={(e) => {
                const scrollLeft = topBarRef.current.scrollLeft
                topBarRef.current.scrollLeft = scrollLeft - 200
                setSlideLeft(scrollLeft)
              }}
            >
              <IconChevronLeft size="md" />
            </div>
          </div>
          <FilterTopBarStyle
            className="scrollbar -mx-4 flex gap-1.5 overflow-auto scroll-smooth px-4 md:mx-0 md:px-0"
            ref={topBarRef}
            topBarStyles={topBarStyles}
          >
            {sortedFilters
              .filter((item) => item.label !== 'Specializations')
              .map((selector) => {
                const key = selector.refinement || selector.attribute
                if (selector.binary) {
                  return (
                    <ConnectedMenuToggle
                      key={key}
                      {...selector}
                      searchState={searchState}
                      hiddenAttributes={hiddenAttributes}
                      operator={selector.operator}
                      customRefine={(label, getTaxonomy) =>
                        setSearchState(label, getTaxonomy, facetPage)
                      }
                      taxonomy={selector}
                      isSelected={selector.isSelected}
                    />
                  )
                }
                return (
                  <div
                    key={key}
                    className={key === 'locations' ? 'hidden' : ''}
                  >
                    <ConnectedMultiSelectList
                      key={key}
                      {...selector}
                      limit={100}
                      searchState={searchState}
                      hiddenAttributes={hiddenAttributes}
                      operator={selector.operator}
                      locationName={locationName}
                      geoLocation={geoLocation}
                      clearFilters={clearFilters}
                      setSearchState={(label, getTaxonomy) =>
                        setSearchState(label, getTaxonomy, facetPage)
                      }
                      isSelected={selector.isSelected}
                      hideSubstanceUseTerms={
                        department === Department.MentalHealth
                      }
                    />
                  </div>
                )
              })}
          </FilterTopBarStyle>
          {/* RightArrow */}
          <div
            className={`absolute right-0 top-0 hidden h-[62px] w-20 shrink-0 items-end justify-end bg-gradient-to-l from-white via-white to-transparent md:flex md:items-center ${
              slideLeft <
              topBarRef.current?.scrollWidth - topBarRef.current?.clientWidth
                ? ''
                : 'md:hidden'
            }`}
          >
            <div
              className={`z-10 mb-0.5 flex size-9
              shrink-0 cursor-pointer items-center justify-center rounded-full border border-gray-400 bg-white px-1 py-2
              shadow-md hover:bg-gray-50 active:bg-gray-200 active:shadow-none`}
              onClick={(e) => {
                const scrollLeft = topBarRef.current.scrollLeft
                topBarRef.current.scrollLeft = scrollLeft + 200
                setSlideLeft(scrollLeft)
              }}
            >
              <IconChevronRight size="md" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FilterTopBar

const MultiSelectList = ({
  attribute,
  multiSelect,
  label,
  items,
  selectedItems,
  refine,
  itemBlacklist,
  itemWhitelist,
  hiddenAttributes,
  searchForItems,
  searchable,
  extractData,
  locationName,
  geoLocation,
  topBarRef,
  clearFilters,
  setSearchState,
  hideSubstanceUseTerms = false
}) => {
  const primaryFocusMap = useMemo(() => createPrimaryFocusMap(), [])

  items = useMemo(
    () =>
      extractData(
        getAllowedFilters(
          items,
          itemWhitelist,
          itemBlacklist,
          (l) => l.label,
          hideSubstanceUseTerms,
          primaryFocusMap
        )
      ),
    [items]
  )

  const selectedCount = selectedItems.length

  const [buttonStyles, setButtonStyles] = useState({})
  const isLocationFilter = attribute === 'locations'

  const isUsingGeolocation = isLocationFilter && !!geoLocation
  const filtersSelected = selectedCount > 0
  const showLocationSelectedByDefault =
    attribute === 'locations' && selectedCount === 0
  const showOptionSelected =
    showLocationSelectedByDefault || filtersSelected || isUsingGeolocation

  const useSelectedItemOrLabel = (selectedItems, label) => {
    if (!isLocationFilter && selectedItems.length === 1) {
      return selectedItems[0]
    }
    return label
  }

  const buttonLabel = isLocationFilter
    ? multiSelectLocationSelectionText({
        selectedItems,
        label,
        isUsingGeolocation,
        locationName
      })
    : useSelectedItemOrLabel(selectedItems, label)

  const hideTooltip = buttonLabel?.length < 23

  // to fix weird button color style issue on page refresh
  useEffect(() => {
    setButtonStyles(`
    ${
      showOptionSelected
        ? 'bg-primary-100 hover:bg-primary-150 active:bg-primary-200 border-transparent focus:border-primary-400'
        : 'bg-white hover:bg-gray-100 active:bg-gray-200 focus:border-gray-800'
    }
  `)
  }, [selectedCount])

  const [searchQuery, setSearchQuery] = React.useState('')

  const [referenceElement, setReferenceElement] = React.useState(null)
  const [popperElement, setPopperElement] = React.useState(null)
  const { styles, attributes: popperAttributes } = usePopper(
    referenceElement,
    popperElement,
    {
      placement: 'bottom-start',
      modifiers: [
        {
          name: 'offset',
          enabled: true,
          options: {
            offset: [0, 5]
          }
        }
      ]
    }
  )

  if (hiddenAttributes.includes(attribute)) {
    return null
  }

  return (
    <div className="pb-0.5">
      <Downshift
        itemToString={(item) => item?.label}
        id={useStaticRandomId()}
        onOuterClick={() => {
          setSearchQuery('')
          searchForItems('')
        }}
      >
        {({ getToggleButtonProps, isOpen, getItemProps }) => {
          const toggleProps = getToggleButtonProps()
          return (
            <div>
              <Tooltip
                text={buttonLabel}
                placement="bottom"
                hidden={hideTooltip}
              >
                <button
                  className={`flex h-9 max-w-[192px] cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-gray-400 py-1.5 pl-3 pr-2 text-sm font-medium  text-gray-1100 lg:max-w-[208px]  ${
                    buttonStyles || ''
                  }`}
                  ref={setReferenceElement}
                  onClick={(e) => {
                    toggleProps.onClick(e)
                  }}
                >
                  <div className="overflow-hidden text-ellipsis">
                    {buttonLabel}
                  </div>
                  {selectedCount > 0 && multiSelect && (
                    <div className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary-200 text-[10px] leading-4">
                      {selectedCount}
                    </div>
                  )}
                  <span className="flex size-4 items-center justify-center">
                    <IconChevronDownChipFilter />
                  </span>
                </button>
              </Tooltip>
              {isOpen && (
                <>
                  {isPhoneScreen() ? (
                    <div>
                      <FilterBottomSheet
                        setPopperElement={setPopperElement}
                        popperAttributes={popperAttributes}
                        label={label}
                        toggleProps={toggleProps}
                        selectedItems={selectedItems}
                        refine={refine}
                        searchable={searchable}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        searchForItems={searchForItems}
                        items={items}
                        getItemProps={getItemProps}
                        onSelectionChange={setSearchState}
                        multiSelect={multiSelect}
                        formatCount={formatCount}
                        topBarRef={topBarRef}
                        clearFilters={clearFilters}
                        taxonomy={attribute}
                        setSearchState={setSearchState}
                      ></FilterBottomSheet>
                    </div>
                  ) : (
                    <FilterDropdown
                      setPopperElement={setPopperElement}
                      popperAttributes={popperAttributes}
                      label={label}
                      toggleProps={toggleProps}
                      selectedItems={selectedItems}
                      refine={refine}
                      searchable={searchable}
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      searchForItems={searchForItems}
                      items={items}
                      getItemProps={getItemProps}
                      onSelectionChange={setSearchState}
                      multiSelect={multiSelect}
                      formatCount={formatCount}
                      topBarRef={topBarRef}
                      clearFilters={clearFilters}
                      popperStyles={styles.popper}
                      taxonomy={attribute}
                      setSearchState={setSearchState}
                    ></FilterDropdown>
                  )}
                </>
              )}
            </div>
          )
        }}
      </Downshift>
    </div>
  )
}

const multiSelectLocationSelectionText = ({
  selectedItems,
  label,
  isUsingGeolocation,
  locationName
}) => {
  if (isUsingGeolocation) {
    return `Near ${locationName}`
  }
  if (selectedItems.length === 0) {
    return WORLDWIDE_SEARCH_LOCATION_NAME
  }
  if (selectedItems.length > 0) {
    return selectedItems.join(', ')
  }

  return label
}

const searchInputDebounceMs = 500

export const SearchInput = ({
  label,
  searchQuery,
  setSearchQuery,
  searchForItems,
  setIsLoading
}) => {
  const inputRef = useRef()
  const firstUpdate = useRef(true)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }
    setIsLoading(true)
    const delayDebounceFn = setTimeout(() => {
      searchForItems(searchQuery)
    }, searchInputDebounceMs)
    return () => clearTimeout(delayDebounceFn)
  }, [searchQuery])

  const handleInputChange = (e) => {
    setSearchQuery(e.currentTarget.value)
  }

  return (
    <div className="flex h-11 items-center self-stretch rounded-xl bg-gray-100 py-0 pl-4 pr-1 md:h-12 md:min-h-[48px] md:rounded-none md:border-b md:border-gray-300 md:bg-transparent">
      <Search className="size-4" />
      <input
        className="w-full bg-transparent text-base font-normal text-gray-1100 outline-none placeholder:text-gray-600 md:text-sm"
        ref={inputRef}
        placeholder={`Search ${label} `}
        type="search"
        onChange={handleInputChange}
      />
    </div>
  )
}

export const NoItemsAvailableMessage = ({ label = '' }) => (
  <div className="flex shrink grow items-start justify-center overflow-auto p-3 md:items-center">
    <div className="flex flex-col items-center justify-center gap-1 py-16 md:py-10">
      <div className="text-base font-medium text-black md:text-sm">
        No {label}
      </div>
      <div className="w-[233px] text-center text-base font-normal text-gray-800 md:text-xs">
        No {label.toLowerCase()} available with the current selection.
      </div>
    </div>
  </div>
)

export const NoSearchResultsMessage = ({ searchQuery, label }) => (
  <div className="flex shrink grow items-start justify-center overflow-auto p-3 md:items-center">
    <div className="flex flex-col items-center justify-center gap-1 py-16 md:py-10">
      <div className="text-base font-medium text-black md:text-sm">
        No results for "{searchQuery}"
      </div>
      <div className="text-base font-normal text-gray-800 md:text-xs">
        Try searching for another {label}.
      </div>
    </div>
  </div>
)

export const ConnectedMultiSelectList = connectRefinementList(MultiSelectList)

const MenuToggle = ({
  label,
  refine,
  refinement,
  taxonomy,
  hiddenAttributes,
  attribute,
  customRefine,
  isSelected
}) => {
  if (hiddenAttributes.includes(attribute)) {
    return null
  }

  return (
    <div className="pb-0.5">
      <button
        className={`flex h-9 cursor-pointer items-center whitespace-nowrap rounded-full border border-gray-400 px-3 py-1.5 text-sm font-medium text-gray-1100 ${
          isSelected
            ? 'border-transparent bg-primary-100 hover:bg-primary-150 focus:border-primary-400 active:bg-primary-200'
            : 'bg-white hover:bg-gray-100 focus:border-gray-800 active:bg-gray-200'
        }`}
        onClick={() => {
          if (customRefine) {
            customRefine(taxonomy.refinement, () => ({
              taxonomy: taxonomy.attribute,
              value: isSelected ? [] : [taxonomy.refinement]
            }))
          } else {
            refine(refinement)
          }
        }}
      >
        <div>{label}</div>
      </button>
    </div>
  )
}

const ConnectedMenuToggle = connectRefinementList(MenuToggle)

const FilterTopBarStyle = styled.div`
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  ${(props) => props.topBarStyles};
`
